/**
 * AdminLayout — unified left-nav sidebar for all admin screens.
 *
 * Applied automatically to every Admin/* Inertia page via application.js resolve().
 * Individual pages render as {children} inside <main>.
 *
 * Features:
 *  • Hamburger (☰ / ←) in the sidebar header to collapse to a 52px icon rail
 *  • Collapsed state persisted in localStorage ('admin_nav_collapsed')
 *  • Active route highlight via window.location.pathname
 *  • 6 top-level sections: Queue · Schedule · Network · GDS Ops · AR Sim · Admin
 *  • GDS Ops sub-sections: Passengers · Ground Ops · Analytics · Integrations
 *  • Smooth width transition (CSS transition, no layout jump)
 */

import { useState, useEffect } from 'react'

// ─── Nav definition ──────────────────────────────────────────────────────────

const NAV = [
  {
    key: 'offers',
    icon: '🏷️', label: 'Offers',
    items: [
      { href: '/offers', icon: '🏷️', label: 'Offers' },
    ],
  },
  {
    key: 'queue',
    icon: '🔄', label: 'Queue',
    items: [
      { href: '/admin/queue_monitor',         icon: '🔄', label: 'Queue Monitor' },
      { href: '/admin/queue_monitor/history', icon: '📋', label: 'History' },
      { href: '/admin/queue_map_monitor',     icon: '🗺️', label: 'Map Monitor' },
    ],
  },
  {
    key: 'schedule',
    icon: '📅', label: 'Schedule',
    items: [
      { href: '/admin/schedule_monitor', icon: '📅', label: 'Schedule Monitor' },
    ],
  },
  {
    key: 'network',
    icon: '🌐', label: 'Network',
    items: [
      { href: '/admin/network_monitor', icon: '🌐', label: 'Network Monitor' },
    ],
  },
  {
    key: 'gds',
    icon: '🛰', label: 'GDS Ops',
    items: [
      { href: '/admin/cloudtraq_gds',                    icon: '📊', label: 'Command Center' },
      { divider: 'Passengers' },
      { href: '/admin/cloudtraq_gds/high_risk',          icon: '⚠️', label: 'High Risk PAX' },
      { href: '/admin/cloudtraq_gds/itinerary',          icon: '📋', label: 'Master PNR' },
      { href: '/admin/cloudtraq_gds/pnr_mutations',      icon: '✈️', label: 'PNR Mutations' },
      { divider: 'Ground Ops' },
      { href: '/admin/cloudtraq_gds/channel_sensor',     icon: '📡', label: 'channelSensor Ops' },
      { href: '/admin/cloudtraq_gds/node_heat_map',      icon: '🗺️', label: 'Node Heat Map' },
      { href: '/admin/cloudtraq_gds/dispatch_board',     icon: '🚀', label: 'Dispatch Board' },
      { divider: 'Analytics' },
      { href: '/admin/cloudtraq_gds/skill_feed',         icon: '⚡', label: 'Skill Feed' },
      { href: '/admin/cloudtraq_gds/carrier_metrics',    icon: '📈', label: 'Carrier Metrics' },
      { href: '/admin/cloudtraq_gds/anc_revenue',         icon: '💰', label: 'Anc Revenue' },
      { divider: 'Integrations' },
      { href: '/admin/cloudtraq_gds/third_party/sabre',  icon: '🏢', label: 'Third-Party Panels' },
    ],
  },
  {
    key: 'arsim',
    icon: '⬡', label: 'AR Sim',
    items: [
      { href: '/admin/ar_simulator', icon: '⬡',  label: 'AR Simulator' },
      { href: '/admin/ar_nodes',     icon: '🗂️', label: 'SensePoints'   },
    ],
  },
  {
    key: 'admin',
    icon: '👤', label: 'Admin',
    items: [
      { href: '/admin/users', icon: '👤', label: 'Users' },
      { href: '/admin/menus', icon: '📄', label: 'Menus' },
    ],
  },
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isActive(href) {
  if (typeof window === 'undefined') return false
  return window.location.pathname === href
}

function sectionIsActive(section) {
  if (typeof window === 'undefined') return false
  const path = window.location.pathname
  return section.items.some(item => item.href && path.startsWith(item.href))
}

// ─── AdminLayout ─────────────────────────────────────────────────────────────

export default function AdminLayout({ children }) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('admin_nav_collapsed') === 'true' } catch { return false }
  })

  // Sync collapsed state to localStorage
  useEffect(() => {
    try { localStorage.setItem('admin_nav_collapsed', String(collapsed)) } catch {}
  }, [collapsed])

  const EXPANDED_W = 236
  const COLLAPSED_W = 52
  const width = collapsed ? COLLAPSED_W : EXPANDED_W

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F8FAFC' }}>

      {/* ── Sidebar ── */}
      <aside
        style={{
          width,
          flexShrink: 0,
          background: '#0F1629',
          borderRight: '1px solid #1E2D52',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s cubic-bezier(0.4,0,0.2,1)',
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          zIndex: 40,
        }}
      >
        {/* Brand + hamburger */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            padding: collapsed ? '14px 0' : '14px 14px',
            borderBottom: '1px solid #1E2D52',
            flexShrink: 0,
          }}
        >
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: '#E94560', boxShadow: '0 0 8px #E94560',
                flexShrink: 0,
              }} />
              <span style={{
                fontFamily: "'Barlow Condensed', system-ui, sans-serif",
                fontWeight: 800, fontSize: 17, letterSpacing: '0.02em',
                color: '#F3F4F6', whiteSpace: 'nowrap',
              }}>
                offerTraQ
              </span>
            </div>
          )}
          <button
            onClick={() => setCollapsed(v => !v)}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#4A5568', fontSize: 20, lineHeight: 1,
              padding: '2px 4px',
              borderRadius: 4,
              transition: 'color 0.15s',
              flexShrink: 0,
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#9CA3AF'}
            onMouseLeave={e => e.currentTarget.style.color = '#4A5568'}
            title={collapsed ? 'Expand navigation (hamburger)' : 'Collapse navigation'}
          >
            {collapsed ? '☰' : '←'}
          </button>
        </div>

        {/* Nav items */}
        <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingTop: 8 }}>
          {NAV.map(section => {
            const sActive = sectionIsActive(section)
            return (
              <div key={section.key} style={{ marginBottom: 4 }}>

                {/* Section group label (expanded) or section icon (collapsed) */}
                {collapsed ? (
                  // Collapsed: show section icon as a clickable group header
                  <div
                    style={{
                      display: 'flex', justifyContent: 'center',
                      padding: '4px 0 2px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 14,
                        opacity: sActive ? 1 : 0.45,
                        transition: 'opacity 0.15s',
                      }}
                      title={section.label}
                    >
                      {section.icon}
                    </span>
                  </div>
                ) : (
                  <p style={{
                    padding: '6px 14px 2px',
                    fontSize: 10, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '0.1em',
                    color: sActive ? '#7C3AED' : '#2D3A5A',
                    whiteSpace: 'nowrap',
                  }}>
                    {section.label}
                  </p>
                )}

                {/* Items */}
                {section.items.map((item, idx) => {
                  if (item.divider) {
                    if (collapsed) return null
                    return (
                      <p key={`div-${idx}`} style={{
                        padding: '8px 14px 2px',
                        fontSize: 9, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.1em',
                        color: '#1E3A5A',
                      }}>
                        {item.divider}
                      </p>
                    )
                  }

                  const active = isActive(item.href)
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      title={collapsed ? item.label : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: collapsed ? 0 : 9,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        padding: collapsed ? '7px 0' : '6px 14px 6px 18px',
                        borderLeft: active ? '3px solid #7C3AED' : '3px solid transparent',
                        background: active ? '#7C3AED18' : 'transparent',
                        color: active ? '#C4B5FD' : '#6B7280',
                        textDecoration: 'none',
                        fontSize: 12.5,
                        fontWeight: active ? 600 : 400,
                        whiteSpace: 'nowrap',
                        transition: 'background 0.12s, color 0.12s, border-color 0.12s',
                      }}
                      onMouseEnter={e => {
                        if (!active) {
                          e.currentTarget.style.background = '#1E2D52'
                          e.currentTarget.style.color = '#D1D5DB'
                        }
                      }}
                      onMouseLeave={e => {
                        if (!active) {
                          e.currentTarget.style.background = 'transparent'
                          e.currentTarget.style.color = '#6B7280'
                        }
                      }}
                    >
                      <span style={{ fontSize: 14, flexShrink: 0 }}>{item.icon}</span>
                      {!collapsed && (
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.label}
                        </span>
                      )}
                    </a>
                  )
                })}

              </div>
            )
          })}
        </nav>

        {/* Footer */}
        <div style={{ borderTop: '1px solid #1E2D52', padding: collapsed ? '10px 0' : '10px 0', flexShrink: 0 }}>
          <a
            href="/"
            title="Home"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: collapsed ? 0 : 9,
              justifyContent: collapsed ? 'center' : 'flex-start',
              padding: collapsed ? '6px 0' : '6px 14px 6px 18px',
              color: '#374151',
              textDecoration: 'none',
              fontSize: 12,
              whiteSpace: 'nowrap',
              transition: 'color 0.12s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#9CA3AF'}
            onMouseLeave={e => e.currentTarget.style.color = '#374151'}
          >
            <span style={{ fontSize: 14, flexShrink: 0 }}>🏠</span>
            {!collapsed && <span>Home</span>}
          </a>
        </div>
      </aside>

      {/* ── Main content ── */}
      <main style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
        {children}
      </main>

    </div>
  )
}
